import { CanvasElements } from '@core/app/constants/canvasElements';
import NS from '@core/app/constants/namespaces';
import layerManager from '@core/app/svgedit/layer/layerManager';
import { getBBoxFromElements } from '@core/app/svgedit/utils/getBBox';

import { CONTOUR_ELEMENT_SELECTOR } from '../constants';
import type { BBox } from '../store';

import { measureWithLayersShown } from './measure';

/**
 * Copy the markup by setting innerHTML on a temporary container, so we can freeze the cut geometry into the saved config, for the repeat
 * use of the same design on the same sheet layout. The markup is a list of strings to avoid the need for a single large string literal in the config.
 */
const parseContourElements = (markup: string[]): SVGGElement => {
  const container = document.createElementNS(NS.SVG, 'g') as SVGGElement;

  container.innerHTML = markup.join('');

  return container;
};

/**
 * The top level cut geometry in layer mode: the elements frozen when the sheets
 * were printed when the config holds them, otherwise the live layer's own
 * children. Top level (rather than the drawable leaves) so a clone keeps any
 * group transforms intact.
 */
export const getContourElements = (
  contourElements: null | string[],
  contourLayerName: null | string,
): SVGGraphicsElement[] => {
  const root = contourElements
    ? parseContourElements(contourElements)
    : contourLayerName
      ? layerManager.getLayerElementByName(contourLayerName)
      : null;

  if (!root) return [];

  return [...root.children].filter((child) => !CanvasElements.defElems.includes(child.tagName)) as SVGGraphicsElement[];
};

/**
 * Bounding box of the cut geometry in canvas units (px), or null when there is
 * none. The chosen layer can sit outside the collected design — it is excluded
 * from the design layers when it was generated by a previous run, and it may be
 * hidden — so the sheet layout has to account for it separately.
 */
export const getContourLayerBBox = (contourElements: null | string[], contourLayerName: null | string): BBox | null => {
  const elements = getContourElements(contourElements, contourLayerName);

  if (elements.length === 0) return null;

  const bbox = measureWithLayersShown(() => getBBoxFromElements(elements));

  // an unmeasurable layer would otherwise stretch the layout box to the origin
  return bbox.width === 0 && bbox.height === 0 ? null : bbox;
};

/**
 * The drawable elements of the cut geometry, for the preview highlight: groups
 * are descended into, so what comes back is what actually gets stroked.
 */
export const getContourPathElements = (
  contourElements: null | string[],
  contourLayerName: null | string,
): SVGGraphicsElement[] =>
  getContourElements(contourElements, contourLayerName).flatMap((element) =>
    element.matches(CONTOUR_ELEMENT_SELECTOR)
      ? [element]
      : ([...element.querySelectorAll(CONTOUR_ELEMENT_SELECTOR)] as SVGGraphicsElement[]),
  );
